import React, { useCallback } from 'react';
import styled from 'styled-components';
import Select from 'components/UI/Select';
import { Space } from 'styles/themeSpaces';
import { Text } from 'styles/themeColors';
import { GENERAL_CONSTANTS } from 'constants/general';
import { ORGANIZATION_CONSTANTS } from 'constants/organization';

// Component key prefix for forcing re-render on disabled state change
const COMPONENT_KEY_PREFIX = 'business-unit-select';

const BusinessUnitSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Space.spHalf};
  width: 259px;
`;

const SelectLabel = styled.div`
  font-size: 14px;
  color: ${Text.primary};
  font-weight: 500;
`;

interface BusinessUnitSelectionDropdownProps {
  selectedBusinessUnitName: string;
  businessUnitOptions: Array<{ value: string; id: string; disabled?: boolean }>;
  onBusinessUnitChange: (businessUnitName: string | null) => void;
  disabled?: boolean;
  isAdminView?: boolean;
  showAllOption?: boolean;
}

/**
 * Business unit selection dropdown component
 *
 * Handles business unit selection with proper styling and event handling
 */
const BusinessUnitSelectionDropdown: React.FC<BusinessUnitSelectionDropdownProps> = ({
  selectedBusinessUnitName,
  businessUnitOptions,
  onBusinessUnitChange,
  disabled = false,
  isAdminView = false,
  showAllOption = true
}) => {
  const handleBusinessUnitChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const unitId = event.target.id;
    // If "All" is selected, pass null to indicate no specific business unit is selected
    if (unitId === 'all') {
      onBusinessUnitChange(null);
    } else {
      onBusinessUnitChange(unitId);
    }
  }, [onBusinessUnitChange]);

  const displayValue = isAdminView
    ? GENERAL_CONSTANTS.ALL
    : (selectedBusinessUnitName || (showAllOption ? GENERAL_CONSTANTS.ALL : ''));

  return (
    <BusinessUnitSelectContainer>
      <SelectLabel>{ORGANIZATION_CONSTANTS.BUSINESS_UNIT}</SelectLabel>
      <Select
        id='top-business-unit-selector'
        key={`${COMPONENT_KEY_PREFIX}-${disabled}`}
        value={displayValue}
        onChange={handleBusinessUnitChange}
        options={businessUnitOptions}
        width="100%"
        height="36px"
        noBorder={false}
        placeholder={GENERAL_CONSTANTS.SELECT_BUSINESS_UNIT}
        disabled={disabled}
      />
    </BusinessUnitSelectContainer>
  );
};

BusinessUnitSelectionDropdown.displayName = 'BusinessUnitSelectionDropdown';

export default BusinessUnitSelectionDropdown;